import Firebase from 'config/Firebase';
import {
  getEnvironments,
  getSecure,
} from 'store/useAtomOutsideComponent';
import { getEnvironmentsConfigApp } from './environment';
import { getBootstrapToken } from './methods';
import { getStringFromLocalStorage } from './localStorage';

const TOKEN_POLL_MS = 250;
const TOKEN_WAIT_MS = 5000;
const WATCH_MAX_MS = 30000;

let loadPromise: Promise<boolean> | null = null;
let watchStop: (() => void) | null = null;

export const isSecureConfigLoaded = (): boolean => {
  if (!getSecure()) return false;
  const env = getEnvironments({});
  return typeof env === 'object' && Object.keys(env).length > 0;
};

const resolveToken = async (): Promise<string | undefined> => {
  const token = await getBootstrapToken();
  if (token) return token;

  const stored = getStringFromLocalStorage('token');
  return stored || undefined;
};

const waitForAuthToken = (maxWaitMs = TOKEN_WAIT_MS): Promise<string | undefined> =>
  new Promise(resolve => {
    let settled = false;

    const settle = (token?: string) => {
      if (settled) return;
      settled = true;
      clearInterval(pollId);
      clearTimeout(timeoutId);
      unsubscribe?.();
      resolve(token);
    };

    const tryResolve = async () => {
      const token = await resolveToken();
      if (token) settle(token);
    };

    const pollId = setInterval(() => {
      void tryResolve();
    }, TOKEN_POLL_MS);

    const timeoutId = setTimeout(() => settle(undefined), maxWaitMs);

    const unsubscribe = Firebase.auth.onAuthStateChanged(user => {
      if (!user) return;
      void user.getIdToken().then(token => {
        if (token) settle(token);
      });
    });

    void tryResolve();
  });

const fetchSecureConfig = async (token: string): Promise<boolean> => {
  try {
    await getEnvironmentsConfigApp({ token });
    return true;
  } catch (error) {
    console.error('ensureSecureConfigLoaded failed:', error);
    return false;
  }
};

type EnsureOptions = {
  /** Espera brevemente a que aparezca un token (p. ej. tras login). */
  waitForToken?: boolean;
};

export const ensureSecureConfigLoaded = async (
  options: EnsureOptions = {},
): Promise<boolean> => {
  if (isSecureConfigLoaded()) return true;

  const { waitForToken = false } = options;

  if (!loadPromise) {
    loadPromise = (async () => {
      let token = await resolveToken();

      if (!token && waitForToken) {
        token = await waitForAuthToken();
      }

      if (!token) return false;

      return fetchSecureConfig(token);
    })().finally(() => {
      loadPromise = null;
    });
  }

  return loadPromise;
};

export const waitUntilSecureConfigLoaded = (
  maxWaitMs = 15000,
): Promise<boolean> =>
  new Promise(resolve => {
    if (isSecureConfigLoaded()) {
      resolve(true);
      return;
    }

    const startedAt = Date.now();

    const interval = setInterval(() => {
      if (isSecureConfigLoaded()) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - startedAt >= maxWaitMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });

/** Observa token en localStorage / Firebase y carga secrets cuando estén disponibles. */
export const startSecureConfigWatcher = (): (() => void) => {
  if (watchStop) return watchStop;

  const startedAt = Date.now();

  const tick = async () => {
    if (isSecureConfigLoaded()) {
      stop();
      return;
    }

    if (Date.now() - startedAt > WATCH_MAX_MS) {
      stop();
      return;
    }

    const token = await resolveToken();
    if (token) {
      await fetchSecureConfig(token);
    }
  };

  const pollId = setInterval(() => {
    void tick();
  }, TOKEN_POLL_MS);

  const unsubscribe = Firebase.auth.onAuthStateChanged(user => {
    if (user) void tick();
  });

  const stop = () => {
    clearInterval(pollId);
    unsubscribe();
    watchStop = null;
  };

  void tick();
  watchStop = stop;
  return stop;
};
